import { useState, forwardRef, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Masonry from 'react-masonry-css';
import { workSections, ImageConfig } from '../../../helpers/images.helper';
import ImagePreviewer from '../../../components/previewers/ImagePreviewer';
import Skeleton from '../../../components/skeleton/Skeleton';
import Footer from '../../../components/footer/Footer';
import { useSEO } from '../../../hooks/useSEO';
import './PortfolioSection.scss';
import { useLanguage } from '../../../contexts/language/LanguageContext';

const PortfolioSection = forwardRef<HTMLDivElement>((_props, ref) => {
  const { t } = useLanguage();
  const location = useLocation();

  // SEO optimization for /portfolio route
  useSEO({
    title: location.pathname === '/portfolio' ? 'Portfolio - ALINA POLONEYEVA' : 'ALINA POLONEYEVA',
    description: location.pathname === '/portfolio'
      ? 'Modeling portfolio of Ukrainian visual creator Alina Poloneyeva. Editorial fashion, runway shows, fashion content from Warsaw and Paris.'
      : 'VISUAL CREATOR / MODEL / FASHION WEEK PRESENCE',
    keywords: 'Alina Poloneyeva portfolio, Ukrainian model portfolio, modeling portfolio, fashion content, editorial fashion, runway modeling, fashion week, Warsaw model, Paris model',
    ogTitle: location.pathname === '/portfolio' ? 'Portfolio - ALINA POLONEYEVA' : 'ALINA POLONEYEVA',
    ogDescription: location.pathname === '/portfolio'
      ? 'Modeling portfolio featuring editorial fashion and runway work'
      : 'VISUAL CREATOR / MODEL / FASHION WEEK PRESENCE',
    ogImage: 'https://alinapoloneyeva.com/images/modeling/modeling_1.jpg',
    ogUrl: location.pathname === '/portfolio' ? 'https://alinapoloneyeva.com/portfolio' : 'https://alinapoloneyeva.com/',
    canonical: location.pathname === '/portfolio' ? 'https://alinapoloneyeva.com/portfolio' : 'https://alinapoloneyeva.com/',
  });
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [allImagesLoaded, setAllImagesLoaded] = useState(false);

  // Only Modeling section images
  const allPortfolioImages: ImageConfig[] = [
    ...workSections[1].images  // Modeling (20 photos)
  ];

  // Preload all images before showing grid
  useEffect(() => {
    let isMounted = true;

    const preloadImages = async () => {
      const imagePromises = allPortfolioImages.map(image => {
        return new Promise<void>((resolve, reject) => {
          const img = new Image();
          img.src = image.src;
          img.onload = () => resolve();
          img.onerror = () => reject();
        });
      });

      try {
        await Promise.all(imagePromises);
        if (isMounted) {
          setAllImagesLoaded(true);
        }
      } catch (error) {
        console.error('Error preloading images:', error);
        // Show images anyway even if some failed
        if (isMounted) {
          setAllImagesLoaded(true);
        }
      }
    };

    preloadImages();

    return () => {
      isMounted = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openLightbox = (index: number) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const handleSlideChange = (newIndex: number) => {
    setCurrentImageIndex(newIndex);
  };

  const breakpointColumns = {
    default: 4,  // 4 photos in a row for all desktop screens
    1024: 2,     // 2 photos for tablets
    640: 1       // 1 photo for mobile
  };

  // Combine refs - use the forwarded ref for parent and local ref for scroll locking
  const combinedRef = (node: HTMLDivElement | null) => {
    sectionRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  };

  return (
    <section className="portfolio-section" ref={combinedRef}>
      <div className="portfolio-section__container">
        <h2 className="portfolio-section__title">{t.portfolio.title}</h2>

        <div className="portfolio-section__section">
          <div className="portfolio-section__section-header">
              <p className="portfolio-section__section-description">{t.portfolio.description.paragraph1}</p>
              <p className="portfolio-section__section-description">{t.portfolio.description.paragraph2}</p>
              {/* <p className="portfolio-section__section-description">{t.about.paragraph3}</p> */}
          </div>

          <Masonry
            breakpointCols={breakpointColumns}
            className="portfolio-section__gallery"
            columnClassName="portfolio-section__gallery-column"
          >
            {allPortfolioImages.map((image, imageIndex) => (
              <div
                key={imageIndex}
                className="portfolio-section__gallery-item"
                onClick={allImagesLoaded ? () => openLightbox(imageIndex) : undefined}
                style={{ cursor: allImagesLoaded ? 'pointer' : 'default' }}
              >
                {!allImagesLoaded ? (
                  <Skeleton variant="card" width="100%" />
                ) : (
                  <img
                    src={image.src}
                    alt={image.alt}
                  />
                )}
              </div>
            ))}
          </Masonry>
        </div>

        <Footer />
      </div>

      <ImagePreviewer
        images={allPortfolioImages}
        isOpen={lightboxOpen}
        currentIndex={currentImageIndex}
        onClose={closeLightbox}
        onSlideChange={handleSlideChange}
        scrollableContainerRef={sectionRef}
      />
    </section>
  );
});

PortfolioSection.displayName = 'PortfolioSection';

export default PortfolioSection;
